import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cmsService, PublicCmsSettings } from "../../services/cmsService";
import Footer from "../../components/layout/Footer";

const Home: React.FC = () => {
  const [cmsSettings, setCmsSettings] = useState<PublicCmsSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCmsSettings();
  }, []);

  const loadCmsSettings = async () => {
    try {
      const settings = await cmsService.getPublicSettings();
      setCmsSettings(settings);
    } catch (err) {
      console.error("Failed to load CMS settings:", err);
      // Continue with default content if CMS fails
    } finally {
      setLoading(false);
    }
  };

  const siteName = cmsSettings?.siteName || "متجرنا";
  const homePageTitle = cmsSettings?.homePageTitle || "مرحباً بكم في متجرنا";
  const homePageSubtitle = cmsSettings?.homePageSubtitle || "";
  const homePageDescription = cmsSettings?.homePageDescription || "صفوا منتجاتنا واطلبوا مباشرة عبر الإنترنت";

  return (
    <div className="home-page bg-background-light dark:bg-background-dark min-h-screen">
      {/* Mobile Top App Bar */}
      <div className="md:hidden flex items-center bg-background-light dark:bg-background-dark p-4 pb-2 justify-between sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-[#0d171b] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
          {siteName}
        </h2>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-background-light dark:bg-background-dark border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 py-4">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            {siteName}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero Section with CMS Image */}
        <section className="hero text-center mb-12 md:mb-16">
          {/* Homepage Image */}
          {cmsSettings?.homePageImage && (
            <div className="mb-8 md:mb-12">
              <img
                src={`data:image/jpeg;base64,${cmsSettings.homePageImage}`}
                alt={homePageTitle}
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-2xl object-contain"
                style={{ maxHeight: "500px" }}
              />
            </div>
          )}

          {/* Title and Content */}
          <h1 className="text-[28px] md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-[#0d171b] dark:text-white tracking-tight leading-tight">
            {homePageTitle}
          </h1>
          
          {homePageSubtitle && (
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4 md:mb-6 text-primary dark:text-primary">
              {homePageSubtitle}
            </h2>
          )}

          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto">
            {homePageDescription}
          </p>
          
          <Link
            to="/catalog"
            className="inline-block bg-primary text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-bold hover:bg-primary/90 transition shadow-lg shadow-primary/30 text-base md:text-lg"
          >
            تصفح الكاتالوج
          </Link>
        </section>

        {/* Features Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-800 hover:shadow-lg transition">
            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#0d171b] dark:text-white">
              طلب سريع
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
              طلب مباشرة من صفحة المنتج، بدون سلة تسوق
            </p>
          </div>
          <div className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-800 hover:shadow-lg transition">
            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#0d171b] dark:text-white">
              توصيل في جميع أنحاء الجزائر
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
              توصيل إلى جميع الولايات والبلديات
            </p>
          </div>
          <div className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-800 hover:shadow-lg transition">
            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#0d171b] dark:text-white">
              خدمة العملاء
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
              فريقنا هنا لمساعدتكم
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Home;
